import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Tag, Users } from 'lucide-react';
import { Button, Input, useToast, Card, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@caruso/ui';
import { clientCategoriesService, ClientCategory, CreateClientCategoryDto, UpdateClientCategoryDto } from '@/services/client-categories.service';

export function ClientCategories() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ClientCategory | null>(null);
  const [formData, setFormData] = useState<CreateClientCategoryDto>({
    name: '',
    description: '',
    color: '#FFD700',
    discount: 0,
    active: true,
  });

  const { data: categories, isLoading } = useQuery({
    queryKey: ['client-categories'],
    queryFn: () => clientCategoriesService.getAll(true),
  });

  const createMutation = useMutation({
    mutationFn: clientCategoriesService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-categories'] });
      toast({
        title: 'Categoria criada!',
        variant: 'success',
      });
      handleCloseDialog();
    },
    onError: () => {
      toast({
        title: 'Erro ao criar categoria',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClientCategoryDto }) =>
      clientCategoriesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-categories'] });
      toast({
        title: 'Categoria atualizada!',
        variant: 'success',
      });
      handleCloseDialog();
    },
    onError: () => {
      toast({
        title: 'Erro ao atualizar categoria',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: clientCategoriesService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-categories'] });
      toast({
        title: 'Categoria excluída!',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Erro ao excluir categoria',
        variant: 'destructive',
      });
    },
  });

  const handleOpenDialog = (category?: ClientCategory) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        color: category.color || '#FFD700',
        discount: category.discount || 0,
        active: category.active,
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        description: '',
        color: '#FFD700',
        discount: 0,
        active: true,
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      color: '#FFD700',
      discount: 0,
      active: true,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCategory) {
      updateMutation.mutate({ id: editingCategory.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta categoria?')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Categorias de Clientes</h1>
          <p className="text-brand-ivory/60 mt-1">
            Gerencie as categorias e benefícios dos seus clientes
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} variant="premium">
          <Plus className="w-4 h-4 mr-2" />
          Nova Categoria
        </Button>
      </div>

      {/* Categories List */}
      {isLoading ? (
        <div className="text-center py-12 text-brand-ivory/50">
          Carregando...
        </div>
      ) : categories?.length === 0 ? (
        <div className="text-center py-12">
          <Tag className="w-16 h-16 text-brand-gold/40 mx-auto mb-4" />
          <p className="text-brand-ivory/50 mb-4">
            Nenhuma categoria encontrada
          </p>
          <Button onClick={() => handleOpenDialog()} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Adicionar primeira categoria
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories?.map((category) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: category.color || '#FFD700' }}
                    />
                    <div>
                      <h3 className="font-serif text-brand-gold text-lg">
                        {category.name}
                      </h3>
                      {!category.active && (
                        <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded">
                          Inativa
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {category.description && (
                  <p className="text-sm text-brand-ivory/60 mb-4">
                    {category.description}
                  </p>
                )}

                <div className="space-y-2 mb-4">
                  {category.discount && category.discount > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-brand-ivory/60">Desconto:</span>
                      <span className="text-brand-gold font-semibold">
                        {category.discount}%
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-brand-ivory/60">Clientes:</span>
                    <span className="text-brand-ivory flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {category._count?.clients || 0}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t border-brand-gold/10">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleOpenDialog(category)}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(category.id)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-brand-ink border-brand-gold/20">
          <DialogHeader>
            <DialogTitle className="text-brand-gold">
              {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-brand-ivory/60 mb-1 block">
                Nome *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: VIP, Premium, Gold..."
                required
                className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
              />
            </div>

            <div>
              <label className="text-sm text-brand-ivory/60 mb-1 block">
                Descrição
              </label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição da categoria"
                className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-brand-ivory/60 mb-1 block">
                  Cor
                </label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-full h-10 rounded-lg bg-brand-smoke/50 border border-brand-gold/20 cursor-pointer"
                />
              </div>

              <div>
                <label className="text-sm text-brand-ivory/60 mb-1 block">
                  Desconto (%)
                </label>
                <Input
                  type="number"
                  value={formData.discount}
                  onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                  min="0"
                  max="100"
                  step="0.1"
                  className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="active" className="text-sm text-brand-ivory/60">
                Categoria ativa
              </label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="premium"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingCategory ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
